import React, { useState, useRef, useMemo } from 'react';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { gamingAPI } from '../services/api';

const WolfPinLink = ({ sessionId, moonlightHost, moonlightVia }) => {
  const [pinDigits, setPinDigits] = useState(['', '', '', '']);
  const [pairResult, setPairResult] = useState(null);
  const pinInputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const shouldFetch = !!sessionId && sessionId !== 'undefined' && sessionId !== 'null';

  const { data: pinData, refetch, isFetching } = useQuery(
    ['wolfPinLink', sessionId],
    () => gamingAPI.getSessionPinLink(sessionId),
    {
      refetchInterval: 15000,
      refetchOnWindowFocus: false,
      retry: 0,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 15000),
      enabled: shouldFetch,
    }
  );

  const tailscaleIp = pinData?.tailscale_ip || pinData?.accessible_ip || null;
  const hexSecret = pinData?.pin_code || null;
  const host = moonlightHost || tailscaleIp;
  const viaWg = moonlightVia === 'wireguard' || (host && String(host).startsWith('10.88.'));
  const hostLabel = viaWg ? '10.88.0.1 (WireGuard)' : host ? `${host} (Tailscale)` : null;

  const pairingPageUrl = useMemo(() => {
    if (!tailscaleIp || !hexSecret) return null;
    const fromApi = pinData?.pin_link;
    if (fromApi && !fromApi.includes('localhost')) {
      return fromApi.includes(tailscaleIp)
        ? fromApi
        : fromApi.replace(/http:\/\/[^:/]+/, `http://${tailscaleIp}`);
    }
    return `http://${tailscaleIp}:47989/pin/#${hexSecret}`;
  }, [tailscaleIp, hexSecret, pinData?.pin_link]);

  const copyPinLink = () => {
    if (!pairingPageUrl) {
      toast.error(
        host
          ? 'Start pairing in Moonlight first (Add PC), then the PIN box is enough.'
          : 'Waiting for Moonlight host 10.88.0.1…'
      );
      return;
    }
    navigator.clipboard.writeText(pairingPageUrl);
    toast.success('Pairing link copied (optional — PIN box below is enough)');
  };

  const openPinLink = () => {
    if (!pairingPageUrl) {
      toast.error(
        host
          ? 'Start pairing in Moonlight first (Add PC), then the PIN box is enough.'
          : 'Waiting for Moonlight host 10.88.0.1…'
      );
      return;
    }
    window.open(pairingPageUrl, '_blank', 'noopener,noreferrer');
  };

  const submitPinMutation = useMutation(
    (pinCode) => gamingAPI.submitPin(sessionId, pinCode),
    {
      onSuccess: (data) => {
        const message = data.message || 'Paired successfully. Moonlight should unlock.';
        toast.success(message);
        setPairResult({
          success: true,
          message,
          pinLink: data.pin_link || pairingPageUrl,
          accessibleIp: data.accessible_ip || data.tailscale_ip || host,
        });
      },
      onError: (err) => {
        const errorMsg = err.response?.data?.detail || err.message || 'Pairing failed';
        toast.error(errorMsg);
        setPairResult({ success: false, message: errorMsg, pinLink: pairingPageUrl });
        setPinDigits(['', '', '', '']);
        pinInputRefs[0].current?.focus();
      },
    }
  );

  const handlePinChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...pinDigits];
    next[index] = value;
    setPinDigits(next);
    if (value && index < 3) pinInputRefs[index + 1].current?.focus();
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      pinInputRefs[index - 1].current?.focus();
    }
    if (e.key === 'Enter' && pinDigits.join('').length === 4) {
      submitPinMutation.mutate(pinDigits.join(''));
    }
  };

  const getFullPin = () => pinDigits.join('');

  const handleSubmitPin = () => {
    const fullPin = getFullPin();
    if (fullPin.length !== 4) {
      toast.error('Enter the 4-digit PIN from Moonlight');
      return;
    }
    submitPinMutation.mutate(fullPin);
  };

  const clearPin = () => {
    setPinDigits(['', '', '', '']);
    setPairResult(null);
    pinInputRefs[0].current?.focus();
  };

  const statusDetail = !host ? 'Waiting for Moonlight host 10.88.0.1…' : null;

  return (
    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-purple-400 font-semibold mb-1">Moonlight Pairing</h4>
          <p className="text-purple-300 text-xs">
            Add the PC in Moonlight with host 10.88.0.1, then enter the 4-digit PIN here. The platform finishes pairing for you.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-xs px-2 py-1 bg-purple-600/30 hover:bg-purple-600/50 rounded text-purple-200"
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="bg-gray-800/50 rounded p-3 mb-3">
        {hostLabel ? (
          <p className="text-emerald-300 text-sm font-mono mb-1">Moonlight host: {host}</p>
        ) : (
          <p className="text-yellow-300 text-sm mb-1">
            Waiting for GPU WireGuard… Import Play on PC from Profile, then use 10.88.0.1.
          </p>
        )}
        <p className="text-amber-200/90 text-xs">
          Do <span className="font-semibold">not</span> open the pairing <code className="text-amber-100">/pin/#…</code> URL
          in a browser — that page expires and shows &quot;key not found&quot;. Use Moonlight + the 4-digit PIN below.
        </p>
        {statusDetail && (
          <p className="text-yellow-200/80 text-xs mt-2">{statusDetail}</p>
        )}
        {/* Advanced: optional hex link kept for debugging only */}
        {pairingPageUrl && (
          <details className="mt-2 text-xs text-gray-500">
            <summary className="cursor-pointer text-gray-400">Advanced (debug only)</summary>
            <div className="mt-1 flex flex-wrap gap-2">
              <button type="button" onClick={copyPinLink} className="px-2 py-1 bg-gray-700 rounded text-gray-300">
                Copy stale pin URL
              </button>
              <button type="button" onClick={openPinLink} className="px-2 py-1 bg-gray-700 rounded text-gray-300">
                Open (will often say key not found)
              </button>
            </div>
            <p className="font-mono break-all mt-1 text-gray-600">{pairingPageUrl}</p>
          </details>
        )}
      </div>

      <div className="bg-blue-900/20 border border-blue-500/20 rounded p-3 mb-3">
        <ol className="text-blue-200 text-xs list-decimal list-inside space-y-1">
          <li>Moonlight → Add PC → paste <span className="font-mono">10.88.0.1</span> (not Tailscale 100.x, not the public IP)</li>
          <li>When Moonlight shows a 4-digit PIN, type it below. Do not click Add PC again.</li>
          <li>Click Submit &amp; Pair once. If Moonlight already shows the tiles, skip PIN — pairing worked.</li>
        </ol>
      </div>

      <div className="bg-gray-800/50 rounded p-4">
        <h5 className="text-purple-400 font-semibold text-sm mb-2">Enter PIN from Moonlight</h5>
        <p className="text-gray-400 text-xs mb-4">
          Use the same 4 digits shown in the Moonlight dialog. Do not open a browser tab for pairing.
        </p>

        <div className="flex justify-center space-x-3 mb-4">
          {pinDigits.map((digit, index) => (
            <input
              key={index}
              ref={pinInputRefs[index]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handlePinChange(index, e.target.value)}
              onKeyDown={(e) => handlePinKeyDown(index, e)}
              className="w-16 h-16 text-center text-2xl font-bold bg-white text-gray-900 rounded-lg border-2 border-purple-500 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="0"
              aria-label={`PIN digit ${index + 1}`}
            />
          ))}
        </div>

        <div className="flex space-x-2 mb-3">
          <button
            type="button"
            onClick={handleSubmitPin}
            disabled={submitPinMutation.isLoading || getFullPin().length !== 4 || !shouldFetch}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded text-white font-semibold"
          >
            {submitPinMutation.isLoading ? 'Pairing… (SSH to GPU)' : 'Submit & Pair'}
          </button>
          <button
            type="button"
            onClick={clearPin}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-gray-200"
          >
            Clear
          </button>
        </div>

        {submitPinMutation.isLoading && (
          <p className="text-yellow-200 text-xs mb-2 text-center">
            Contacting the GPU over SSH — usually a few seconds, not an error.
          </p>
        )}

        {pairResult && (
          <div
            className={`mt-3 p-3 rounded text-sm ${
              pairResult.success
                ? 'bg-green-900/30 text-green-300 border border-green-500/30'
                : 'bg-red-900/30 text-red-300 border border-red-500/30'
            }`}
          >
            <div className="flex items-center space-x-2">
              <span>{pairResult.success ? '✓' : '✗'}</span>
              <span>{pairResult.message}</span>
            </div>
            {pairResult.accessibleIp && (
              <div className="text-xs text-gray-400 mt-2">
                Host: {pairResult.accessibleIp}:47989
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 bg-blue-900/20 border border-blue-500/30 rounded p-3">
        <p className="text-blue-300 text-xs">
          After pairing, Moonlight streams over WireGuard to 10.88.0.1. If video fails with RTSP 10060, Wolf was still advertising the guest LAN IP — refresh Game and reconnect once.
        </p>
      </div>
    </div>
  );
};

export default WolfPinLink;
